import { useContext, useEffect, useState } from 'react';
import { DefaultVersionEnum } from '@api/ringil-api.v2';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { BetaFeaturesMap, BetaSurface, BetaSurfaceKey, BETA_SURFACE_BY_KEY } from './betaSurfaces';

export const betaStorageKey = (surface: BetaSurfaceKey) => `betaSurface:${surface}`;

/**
 * Broadcast so every consumer of a surface reacts to a toggle, not just the one that owns the switch.
 * A surface can be rendered in several places at once (e.g. the notification page and the menu badge),
 * and plain localStorage state is per-hook-instance — without this the others stay stale until reload.
 */
const OVERRIDE_CHANGED_EVENT = 'betaSurface:override-changed';

/** Every write to a surface's stored override announces itself so all mounted consumers re-read it. */
const notifyOverrideChanged = (surface: BetaSurfaceKey) => window.dispatchEvent(new CustomEvent<BetaSurfaceKey>(OVERRIDE_CHANGED_EVENT, { detail: surface }));

const readOverride = (surface: BetaSurfaceKey): DefaultVersionEnum | null => {
  try {
    const raw = window.localStorage.getItem(betaStorageKey(surface));
    return raw ? (JSON.parse(raw) as DefaultVersionEnum) : null;
  } catch {
    return null;
  }
};

const useOverride = (surface: BetaSurfaceKey): [DefaultVersionEnum | null, (value: DefaultVersionEnum | null) => void] => {
  const [override, setOverrideState] = useState<DefaultVersionEnum | null>(() => readOverride(surface));

  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent<BetaSurfaceKey>).detail === surface) setOverrideState(readOverride(surface));
    };
    window.addEventListener(OVERRIDE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(OVERRIDE_CHANGED_EVENT, handler);
  }, [surface]);

  const setOverride = (value: DefaultVersionEnum | null) => {
    try {
      if (value === null) window.localStorage.removeItem(betaStorageKey(surface));
      else window.localStorage.setItem(betaStorageKey(surface), JSON.stringify(value));
    } catch {
      // storage unavailable (private mode / quota) — the in-memory state below still applies
    }
    setOverrideState(value);
    notifyOverrideChanged(surface);
  };

  return [override, setOverride];
};

// Old per-page keys migrated once into the new namespace. Each carries its own converter: the
// LegacyVersionSwitch pages stored a boolean (true = legacy), while the filter switch stored 'legacy'|'v2'.
type LegacyMigration = { key: string; toVersion: (raw: unknown) => DefaultVersionEnum };
const fromLegacyBool = (raw: unknown) => (raw === true ? DefaultVersionEnum.ORIGINAL : DefaultVersionEnum.NEW);
export const LEGACY_STORAGE_KEY_BY_SURFACE: Partial<Record<BetaSurfaceKey, LegacyMigration>> = {
  [BetaSurface.DASHBOARD]: { key: 'Dashboard.showLegacy', toVersion: fromLegacyBool },
  [BetaSurface.COMPANY_LOCATION_DETAIL]: { key: 'LocalityDetail.showLegacy', toVersion: fromLegacyBool },
  [BetaSurface.DATA_TABLE_FILTERS_V2]: { key: 'RINGIL_FILTERS_UI_VERSION', toVersion: raw => (raw === 'legacy' ? DefaultVersionEnum.ORIGINAL : DefaultVersionEnum.NEW) },
};

const migrateLegacyKey = (surface: BetaSurfaceKey) => {
  const newKey = betaStorageKey(surface);
  const entry = LEGACY_STORAGE_KEY_BY_SURFACE[surface];
  if (!entry) return;
  try {
    if (window.localStorage.getItem(newKey) !== null) return;
    const rawOld = window.localStorage.getItem(entry.key);
    if (rawOld === null) return;
    const version = entry.toVersion(JSON.parse(rawOld));
    window.localStorage.setItem(newKey, JSON.stringify(version));
    window.localStorage.removeItem(entry.key);
    notifyOverrideChanged(surface);
  } catch {
    // ignore malformed legacy value
  }
};

interface BetaVersion {
  version: DefaultVersionEnum;
  showLegacy: boolean;
  switchVisible: boolean;
  toggle: () => void;
}

const useBetaVersion = (surface: BetaSurfaceKey): BetaVersion => {
  const { loggedUser, hasFeature } = useContext(AuthenticatedUserContext);

  useEffect(() => {
    migrateLegacyKey(surface);
  }, [surface]);

  const [override, setOverride] = useOverride(surface);

  // Company config is the opaque store (surface → config); unconfigured surfaces fall back to the FE catalog.
  const cfg = (loggedUser?.betaFeatures as BetaFeaturesMap | undefined)?.[surface];
  const fallback = BETA_SURFACE_BY_KEY[surface];
  const companyDefault = cfg?.defaultVersion ?? fallback.defaultVersion;
  // Surfaces with no end-user switch never honour a per-user override — only the company default.
  const switchVisible = fallback.userSwitchable === false ? false : cfg?.switchVisible ?? fallback.switchVisible;

  useEffect(() => {
    if (!switchVisible && override !== null) setOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchVisible]);

  // A surface can name a company feature as its kill switch; without it the config above is discarded.
  const gateClosed = fallback.requiredFeature != null && !hasFeature([fallback.requiredFeature]);

  const version = gateClosed ? DefaultVersionEnum.ORIGINAL : !switchVisible ? companyDefault : override ?? companyDefault;

  const toggle = () => setOverride(version === DefaultVersionEnum.NEW ? DefaultVersionEnum.ORIGINAL : DefaultVersionEnum.NEW);

  return {
    version,
    showLegacy: version === DefaultVersionEnum.ORIGINAL,
    switchVisible,
    toggle,
  };
};

export default useBetaVersion;
